import { G } from '@mobily/ts-belt';
import { Box, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useEffect, useMemo, useState } from 'react';
import { maxValue, minValue, NumberInput, required, useNotify } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';

import { TypographyTranslated } from '@sh4res/bo-common/components';
import { DeceasedEmployeesSection } from '@sh4res/bo-common/components/B2bWithdrawalBulkCreate/steps/DeceasedEmployeesSection';
import {
  BulkReviewGridRow,
  FormValues,
  getDeceasedRowsToWithdraw,
  getGridRow,
  parseBulkFileRows,
} from '@sh4res/bo-common/components/B2bWithdrawalBulkCreate/steps/utils';
import { MaxButton } from '@sh4res/bo-common/fields';
import { useResolveBulkRows, useResourceBasePath } from '@sh4res/bo-common/hooks';

const testId = 'BoExternalB2bWithdrawalBulkCreateReviewStep';

const handleArrowKeyNavigation = (event: React.KeyboardEvent<HTMLDivElement>): void => {
  event.stopPropagation();
};

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const formatPositionSummary = (row: BulkReviewGridRow): string => {
  const totalValue = row.amount_available;
  const totalShares = row.quantity_available;
  const quantityToWithdraw = row.quantity_to_withdraw;

  if (
    !Number.isFinite(totalValue) ||
    !Number.isFinite(totalShares) ||
    !Number.isFinite(quantityToWithdraw)
  ) {
    return '';
  }

  const selectedValue = (quantityToWithdraw / totalShares) * totalValue;
  const selectedValueClamped = Math.max(0, Math.min(selectedValue, totalValue));

  return `${formatCurrency(Number.isNaN(selectedValueClamped) ? 0 : selectedValueClamped)} / ${formatCurrency(totalValue)}`;
};

export function ReviewStep() {
  const notify = useNotify();
  const resourceBasePath = useResourceBasePath();
  const { getValues, setValue, trigger } = useFormContext<FormValues>();

  const companyId = useWatch<FormValues, 'b2b_company_id'>({
    name: 'b2b_company_id',
  });
  const instrumentId = useWatch<FormValues, 'instrument_id'>({
    name: 'instrument_id',
  });
  const uploadedFileValue = useWatch<FormValues, 'bulk_file'>({
    name: 'bulk_file',
  });
  const rows = useWatch<FormValues, 'bulk_rows'>({
    name: 'bulk_rows',
    defaultValue: [],
  });
  const [page, setPage] = useState(0);
  const [rowsParsingLoading, setRowsParsingLoading] = useState(true);
  const [visitedPages, setVisitedPages] = useState<Set<number>>(new Set([0]));
  const pageSize = 25;

  const uploadedFile = uploadedFileValue.rawFile;
  const uploadedFileKey = useMemo(() => {
    if (!(uploadedFile instanceof File)) {
      return null;
    }

    return `${uploadedFile.name}:${uploadedFile.size}:${uploadedFile.lastModified}`;
  }, [uploadedFile]);
  const totalPages = useMemo(() => {
    if (rows.length === 0) {
      return 0;
    }

    return Math.ceil(rows.length / pageSize);
  }, [rows.length, pageSize]);
  const currentPageStart = page * pageSize;
  const currentPageEnd = currentPageStart + pageSize;
  const currentPageRows = useMemo(
    () => rows.slice(currentPageStart, currentPageEnd),
    [rows, currentPageStart, currentPageEnd],
  );

  useEffect(() => {
    if (totalPages === 0) {
      if (page !== 0) {
        setPage(0);
      }
      return;
    }

    const maxPage = totalPages - 1;
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [page, totalPages]);

  useEffect(() => {
    if (totalPages === 0) {
      setVisitedPages(new Set([0]));
      return;
    }

    setVisitedPages((prev) => {
      const next = new Set<number>(prev);
      next.add(page);
      return next;
    });
  }, [page, totalPages]);

  const deceasedRows = useMemo(() => getDeceasedRowsToWithdraw(rows), [rows]);

  const gridRows = useMemo<BulkReviewGridRow[]>(
    () =>
      rows.map<BulkReviewGridRow>((row, index) => ({
        ...row,
        id: index,
      })),
    [rows],
  );

  useEffect(() => {
    const allPagesVisited = totalPages > 0 && visitedPages.size >= totalPages;
    const allDataFetched =
      rows.length > 0 &&
      rows.every((row) => G.isNotNullable(row.employee_id) && row.amount_available > 0);

    if (getValues('bulk_rows_all_pages_visited') !== allPagesVisited) {
      setValue('bulk_rows_all_pages_visited', allPagesVisited, { shouldDirty: false });
    }

    if (getValues('bulk_rows_all_data_fetched') !== allDataFetched) {
      setValue('bulk_rows_all_data_fetched', allDataFetched, { shouldDirty: false });
    }
  }, [getValues, rows, setValue, totalPages, visitedPages]);

  useEffect(() => {
    let isCancelled = false;

    const parseFile = async () => {
      if (!(uploadedFile instanceof File) || G.isNullable(uploadedFileKey)) {
        setRowsParsingLoading(false);
        return;
      }

      const existingRows = getValues('bulk_rows');
      const existingFileKey = getValues('bulk_file_key');
      const hasRows = Array.isArray(existingRows) && existingRows.length > 0;

      // Preserve user-edited review data when returning to this step with the same file.
      if (existingFileKey === uploadedFileKey && hasRows) {
        setRowsParsingLoading(false);
        return;
      }

      try {
        const parsedRows = await parseBulkFileRows(uploadedFile);

        if (!isCancelled) {
          setRowsParsingLoading(false);
          setPage(0);
          setVisitedPages(new Set([0]));
          setValue('bulk_rows', parsedRows, { shouldDirty: true });
          setValue('bulk_file_key', uploadedFileKey, { shouldDirty: false });
        }
      } catch {
        if (!isCancelled) {
          setRowsParsingLoading(false);
          setPage(0);
          setVisitedPages(new Set([0]));
          setValue('bulk_rows', [], { shouldDirty: true });
          setValue('bulk_file_key', undefined, { shouldDirty: false });
          setValue('bulk_rows_all_pages_visited', false, { shouldDirty: false });
          setValue('bulk_rows_all_data_fetched', false, { shouldDirty: false });
          notify('resources.bo_common.b2b.withdrawals.bulk_create.errors.parse_file_failed', {
            type: 'error',
          });
        }
      }
    };

    void parseFile();

    return () => {
      isCancelled = true;
    };
  }, [uploadedFile, uploadedFileKey, getValues, notify, setValue]);

  useResolveBulkRows({
    rows,
    currentPageStart,
    currentPageEnd,
    companyId,
    instrumentId,
    resourceBasePath,
    setValue,
    trigger,
  });

  const currentPageHasUnresolvedEmployeeIds = useMemo(
    () => currentPageRows.some((row) => G.isNullable(row.employee_id)),
    [currentPageRows],
  );

  const currentPageHasResolvedPositions = useMemo(
    () =>
      currentPageRows.some(
        (row) => G.isNotNullable(row.quantity_available) || G.isNotNullable(row.amount_available),
      ),
    [currentPageRows],
  );

  const currentPageHasInvalidQuantities = useMemo(
    () =>
      currentPageRows.some(
        (row) =>
          !(
            Number.isFinite(row.quantity_available) &&
            Number.isFinite(row.quantity_to_withdraw) &&
            row.quantity_available >= 0 &&
            row.quantity_available >= row.quantity_to_withdraw
          ),
      ),
    [currentPageRows],
  );

  const isGridLoading = !currentPageHasResolvedPositions || currentPageHasUnresolvedEmployeeIds;
  const isPaginationBlocked = isGridLoading || currentPageHasInvalidQuantities;

  const columns = useMemo<GridColDef<BulkReviewGridRow>[]>(
    () => [
      {
        field: 'employee',
        headerName: 'Employee',
        minWidth: 300,
        flex: 1,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'baseline',
                gap: '8px',
                fontSize: 11,
              }}>
              <Typography margin={0}>
                {getGridRow(params).firstname} {getGridRow(params).lastname}
              </Typography>
              <Typography margin={0} variant="body2" color="text.secondary">
                {getGridRow(params).nir || getGridRow(params).ntt}
              </Typography>
            </Box>
            <Typography margin={0} sx={{ fontWeight: 'bold', fontSize: 11 }}>
              {getGridRow(params).plan}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'quantity_available',
        headerName: 'Total shares',
        minWidth: 100,
        sortable: false,
        renderCell: (params: GridRenderCellParams) =>
          `${getGridRow(params).quantity_available} Units`,
      },
      {
        field: 'quantity_to_withdraw',
        headerName: 'To withdraw',
        minWidth: 200,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const rowIndex = Number(params.id);
          const maxQty = getGridRow(params).quantity_available;

          return (
            <Box
              sx={{
                width: '150px',
                display: 'grid',
                gridTemplateColumns: 'auto auto',
                gridColumnGap: '10px',
                position: 'relative',
              }}>
              <NumberInput
                source={`bulk_rows.${rowIndex}.quantity_to_withdraw`}
                label={false}
                hiddenLabel
                onKeyDown={handleArrowKeyNavigation}
                validate={[required(), minValue(0), maxValue(maxQty)]}
                min={0}
                max={maxQty}
                data-testid={`${testId}_QuantityInput`}
              />
              <Box margin={'auto'}>Units</Box>
              <MaxButton
                field={`bulk_rows.${rowIndex}.quantity_to_withdraw`}
                value={maxQty}
                data-testid={`${testId}_MaxButton`}
                customStyles={{
                  position: 'absolute',
                  marginTop: 0,
                  top: '10px',
                  right: '48px',
                  marginLeft: 0,
                  marginRight: 0,
                }}
              />
            </Box>
          );
        },
      },
      {
        field: 'amount_available',
        headerName: 'Current position',
        minWidth: 250,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const row = getGridRow(params);

          if (isGridLoading) {
            return '';
          }

          return formatPositionSummary(row);
        },
      },
    ],
    [isGridLoading],
  );

  if (rows.length === 0 && !rowsParsingLoading) {
    return (
      <TypographyTranslated label="resources.bo_common.b2b.withdrawals.bulk_create.labels.no_rows_found" />
    );
  }

  const instrumentName = rows.at(0)?.instrument_name;

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {instrumentName}
      </Typography>
      <TypographyTranslated
        label="resources.bo_common.b2b.withdrawals.bulk_create.labels.review_step_description"
        sx={{ mb: 2 }}
      />
      <DataGrid
        editMode="cell"
        rows={gridRows}
        columns={columns}
        pagination
        page={page}
        rowHeight={70}
        onPageChange={(nextPage) => {
          if (isPaginationBlocked) {
            return;
          }

          setPage(nextPage);
        }}
        loading={rowsParsingLoading}
        pageSize={pageSize}
        autoHeight
        disableSelectionOnClick
        disableColumnMenu
        rowsPerPageOptions={[10]}
        sx={{
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none',
          },
          '& .MuiTablePagination-toolbar': {
            padding: 0,
          },
          // disable pagination while loading or visible row errors
          ...(isPaginationBlocked
            ? {
                '& .MuiTablePagination-root': {
                  pointerEvents: 'none',
                  opacity: 0.6,
                },
              }
            : {}),
        }}
      />
      <DeceasedEmployeesSection deceasedRows={deceasedRows} />
    </Box>
  );
}
